import React, { useState, FormEvent, useEffect } from "react";
import CustomDropdownAssistant from "./CustomDropdownAssistant";
import { useProject } from "@utils/useProject";
import { useReport } from "@utils/useReports";
import { ServerFile } from "@shared/interfaces/ProjectsFiles";
import Loader from "@shared/components/Loader";
import { displayReportName } from "@pages/Project/utils/utils";
import { AssistantSelectedFiles } from "@services/ProjectChatContext";
import MultiSelectDropdownAssistant from "./MultiSelectionDropdownAssistant";
import type { UploadedConversationFile } from "@services/ConversationFilesService";
import { RiCloseCircleFill } from "react-icons/ri";

import SourcesDropdown, {
  SourceOption as SourceOptionType,
} from "./SourcesDropdown";
import { getFileIcon } from "@pages/Project/components/Tabs/FileUploadTab";

export interface Option {
  id: string;
  name: string;
  icon?: string;
}

interface AssistantFormProps {
  handleSubmit: (e: FormEvent<HTMLFormElement>) => void;
  isChatLoading: boolean;
  question: string;
  setQuestion: (value: string) => void;
  selectedFiles: AssistantSelectedFiles[];
  setSelectedFiles: React.Dispatch<
    React.SetStateAction<AssistantSelectedFiles[]>
  >;
  chatScope: string;
  setChatScope: React.Dispatch<React.SetStateAction<string>>;
  selectedProject: Option;
  setSelectedProject: React.Dispatch<React.SetStateAction<Option>>;
  selectedReport: Option;
  setSelectedReport: React.Dispatch<React.SetStateAction<Option>>;
  scopedDefined: boolean;
  enabledSources: Set<string>;
  sourceOptions: SourceOptionType[];
  setEnabledSources: React.Dispatch<React.SetStateAction<Set<string>>>;

  onUploadFiles: (files: FileList) => void;

  pendingFiles: UploadedConversationFile[];
  onDeletePendingFile: (fileId: string) => void;
  deletingFileId?: string | null;
  isFileUploading: boolean;
}

const AssistantForm: React.FC<AssistantFormProps> = ({
  handleSubmit,
  isChatLoading,
  question,
  setQuestion,
  selectedFiles,
  setSelectedFiles,
  setChatScope,
  selectedProject,
  setSelectedProject,
  selectedReport,
  setSelectedReport,
  scopedDefined,
  enabledSources,
  sourceOptions,
  setEnabledSources,

  onUploadFiles,
  pendingFiles,
  onDeletePendingFile,
  deletingFileId,
  isFileUploading,
}) => {
  const [projects, setProjects] = useState<Option[]>([]);
  const [reports, setReports] = useState<Option[]>([]);
  const [files, setFiles] = useState<ServerFile[]>([]);
  const [loading, setLoading] = useState(false);
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);

  const { getProjectsDropdown } = useProject();
  const { getReportsDropdown } = useReport();

  const submitButtonDisabled = !question.trim() || isChatLoading || loading;

  useEffect(() => {
    (async () => {
      const fetchedProjects = await getProjectsDropdown();
      setProjects(
        fetchedProjects.map((project: { id: number; name: string }) => ({
          id: project.id.toString(),
          name: project.name,
        }))
      );
    })();
  }, []);

  useEffect(() => {
    setReports([]);
    (async () => {
      if (scopedDefined) return;
      if (
        !selectedProject.id ||
        selectedProject.id === "all" ||
        selectedProject.id === "0"
      )
        return;

      try {
        setLoading(true);
        const response = await getReportsDropdown(selectedProject.id);
        const mappedReports = response.map((report: any) => ({
          ...report,
          name: displayReportName(
            report.name,
            report.template_name,
            new Date(report.created_at)
          ),
        }));

        setReports(mappedReports);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Error fetching reports or files:", error);
      } finally {
        setLoading(false);
      }
    })();
  }, [selectedProject]);

  const clearSelection = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setSelectedProject({ id: "", name: "Select Project" });
    setSelectedReport({ id: "", name: "" });
    setSelectedFiles([]);
    setEnabledSources(new Set());
    setChatScope("Global");
  };

  useEffect(() => {
    if (selectedFiles.length > 0) {
      setChatScope(
        `Files: ${selectedFiles.map((file) => file.name).join(", ")}`
      );
    } else if (selectedProject.id && !selectedReport.id) {
      setChatScope(selectedProject.name);
    } else if (selectedReport.id) {
      setChatScope(selectedReport.name);
    } else if (!selectedProject.id) {
      setChatScope("Global");
    }
  }, [selectedProject, selectedReport, selectedFiles]);

  return (
    <form
      onSubmit={handleSubmit}
      className="flex flex-col gap-3 p-5 bg-[#F2F4F7] rounded-lg shadow w-full"
    >
      {/* Dropdowns */}
      {!scopedDefined && (
        <div className="flex flex-wrap items-center space-x-2 w-full">
          <div className="flex space-x-4 w-full">
            <div className="flex space-x-2 w-full">
              <CustomDropdownAssistant
                options={[
                  { id: "", name: "Select Project" },
                  { id: "0", name: "All Projects" },
                  ...projects,
                ]}
                value={selectedProject.id}
                onChange={(val) => {
                  setSelectedProject(val);
                  setSelectedReport({} as Option);
                  setSelectedFiles([]);
                }}
                placeholder="Select Project"
                disabled={scopedDefined}
                icon="/folder.svg"
                showOneLine={false}
              />

              <MultiSelectDropdownAssistant
                selected={selectedFiles}
                onChange={setSelectedFiles}
                disabled={
                  scopedDefined ||
                  loading ||
                  !selectedProject.id ||
                  !!selectedReport.id ||
                  selectedProject.id === "0"
                }
                placeholder="Select documents"
                showOneLine={false}
                selectedProject={selectedProject}
              />

              <CustomDropdownAssistant
                options={[{ id: "", name: "Select Report" }, ...reports]}
                value={selectedReport.id}
                onChange={(val) => {
                  setSelectedReport(val);
                }}
                placeholder="Select Report"
                disabled={
                  scopedDefined ||
                  reports.length === 0 ||
                  loading ||
                  !selectedProject.id ||
                  selectedFiles.length > 0 ||
                  selectedProject.id === "0"
                }
                icon="/file-text.svg"
                showOneLine={false}
              />
            </div>

            {(selectedProject.id || enabledSources.size > 0) && (
              <button
                type="button"
                className="text-blue-600 pl-2 pr-4 text-xs 2xl:text-sm font-semibold"
                onClick={clearSelection}
              >
                Clear
              </button>
            )}
          </div>
        </div>
      )}

      {/* Main Input Row */}
      <div className="flex flex-col bg-[#FFFFFE] border border-[#E4E7EC] py-1 pr-2.5 rounded-md">
        {pendingFiles?.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 my-2 px-2.5 ">
            {pendingFiles.map((f) => (
              <span
                key={f.id}
                className="inline-flex relative items-center gap-2 px-2 py-1 text-[11px] 2xl:text-xs rounded-md border border-[#E4E7EC] bg-white"
                title={f.filename}
              >
                <img
                  src={getFileIcon(f.filetype)}
                  alt={`${f.filetype} icon`}
                  className="w-6 h-6 2xl:w-7 2xl:h-7"
                />
                <span className="max-w-[220px] truncate">{f.filename}</span>
                {deletingFileId === String(f.id) ? (
                  <div className="ml-1 opacity-70 hover:opacity-100 absolute top-[-5px] right-[-5px] bg-white rounded-full p-0.5 border border-1">
                    <Loader color="#3981F6" size="xs" />
                  </div>
                ) : (
                  <button
                    type="button"
                    className="ml-1 opacity-70 hover:opacity-100 absolute top-[-8px] right-[-8px]"
                    onClick={() => onDeletePendingFile(String(f.id))}
                    aria-label={`Remove ${f.filename}`}
                  >
                    <RiCloseCircleFill color="red" className="w-4 h-4" />
                  </button>
                )}
              </span>
            ))}
          </div>
        )}
        <div className="flex items-center w-full ">
          <textarea
            className="flex-1 py-2.5 px-2.5 text-xs 2xl:text-sm bg-transparent focus:outline-none disabled:cursor-not-allowed resize-none h-10 leading-[2.5rem] scrollbar-hide"
            placeholder="Type your question..."
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                const form = e.currentTarget.form;
                if (form) form.requestSubmit();
              }
            }}
            disabled={loading || isChatLoading}
          />
          {scopedDefined && (
            <>
              {loading ? (
                <Loader color="#2162EB" />
              ) : (
                <button
                  type="submit"
                  className=" bg-[#0056C8] w-7 h-7 flex items-center text-white p-1 rounded-md text-sm disabled:bg-blue-400 disabled:cursor-not-allowed"
                  disabled={submitButtonDisabled}
                >
                  {isChatLoading ? (
                    <Loader color="#fff" />
                  ) : (
                    <img
                      src="/submit.svg"
                      className="w-3.5 h-3.5 mx-auto"
                      alt=""
                    />
                  )}
                </button>
              )}
            </>
          )}
        </div>

        {!scopedDefined && (
          <div className="flex items-end justify-between pl-2.5 mt-3 mb-1.5">
            <div className="flex space-x-2 ">
              <SourcesDropdown
                options={sourceOptions}
                value={enabledSources}
                onChange={setEnabledSources}
                ariaLabel="Knowledge sources"
                classNameButton="w-8 h-8"
              />

              <div className="flex items-center gap-2">
                {isFileUploading ? (
                  <div className="w-8 h-8 flex items-center justify-center rounded-md border border-[#E4E7EC] bg-white cursor-not-allowed">
                    <Loader size="sm" color="#2162EB" />
                  </div>
                ) : (
                  <button
                    type="button"
                    className="w-8 h-8  rounded-lg border border-gray-300 bg-white cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={isChatLoading || loading}
                    onClick={() => fileInputRef.current?.click()}
                    aria-label="Attach files"
                    title={"Attach files"}
                  >
                    <img
                      src="/paperclip.svg"
                      alt=""
                      className="mx-auto w-4 h-4"
                    />
                  </button>
                )}

                <input
                  ref={fileInputRef}
                  id="chat-file-input"
                  type="file"
                  multiple
                  className="sr-only"
                  onChange={(e) => {
                    if (e.target.files?.length) {
                      onUploadFiles(e.target.files);
                      e.currentTarget.value = ""; // allow re-selecting same file
                    }
                  }}
                  disabled={isChatLoading || loading}
                />
              </div>
            </div>
            {loading ? (
              <Loader color="#2162EB" />
            ) : (
              <button
                type="submit"
                className=" bg-[#0056C8] w-7 h-7 flex items-center text-white p-1.5 rounded-md text-sm disabled:bg-blue-400 disabled:cursor-not-allowed"
                disabled={submitButtonDisabled}
              >
                {isChatLoading ? (
                  <span className="flex">
                    <div className=" flex justify-center items-center">
                      <div className="w-5 h-5 border-2 mr-2 border-t-transparent border-white rounded-full animate-spin"></div>
                    </div>
                  </span>
                ) : (
                  <img
                    src="/submit.svg"
                    className="w-3.5 h-3.5 mx-auto"
                    alt=""
                  />
                )}
              </button>
            )}
          </div>
        )}
      </div>

      {/* //here — Sources (globe) multiselect dropdown with switches */}
    </form>
  );
};

export default AssistantForm;
